import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, Injector } from '@angular/core';
import { BaseComponent } from 'src/app/shared/baseComponent';
// Dynamically import scenario class definitions so we can derive allowed interactivity classes
import * as ScenarioDefs from '../ScenarioClasses';

// Helper type guard to ensure a value is a constructor function
function isCtor(fn: any): fn is new (...args: any[]) => any { return typeof fn === 'function'; }

@Component({
  selector: 'app-interactivities',
  templateUrl: './interactivities.component.html',
  styleUrls: ['./interactivities.component.css']
})
export class InteractivitiesComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() scenario: any;
  // Parent callbacks to persist and reload scenario
  @Input() onSaveInteractivity?: () => Promise<void>;
  @Input() onReloadInteractivity?: () => Promise<void>;

  // UI state
  showInteractivityInfo = false;
  // Removed per-instance enum tracking (derive from class string when needed)
  showInteractivity = false; // base/default editor (shared)
  showInteractivityGrass = false;
  showInteractivityFire = false;
  showInteractivitySmoke = false;
  showInteractivityGateOpenClose = false;
  showInteractivityWindDirection = false;
  showInteractivityWindHose = false;
  showInteractivityCompass = false;
  showInteractivityPPE = false;
  showInteractivitySensorSim = false;
  showInteractivityTracker = false;
  showInteractivityRoles = false;
  showInteractivityScenarioDescription = false;
  showInteractivitySickChar = false;
  showInteractivityModalDisplay = false;
  showInteractivityLabelDisplay = false;
  showInteractivityHyperlink = false;
  showInteractivityExploreArec = false;
  showInteractivityExploreMap = false;
  showInteractivityMap = false;
  showInteractivityApproach = false;
  showInteractivityTankertruck = false;
  showInteractivityManhole = false;
  showInteractivityPhaseInOut = false;
  showInteractivityPlayAnimation = false;
  showInteractivityPlayAudio = false;
  showInteractivityRotate = false;
  showInteractivityShowHide = false;
  showInteractivityBoiler = false;
  showInteractivitySewer = false;
  showInteractivityWeatherStation = false;
  showJsonError = false;
  showComponentList = false;
  // Shared info panel toggle for class/name requirements
  showClassNameInfo = false;
  // pick lists
  allowedModalHtmlFiles: string[] = ['scenarioDescription.html', 'navigationDescription.html', 'inviteWithUrl.html'];
  showModalHtmlFileList = false;
  allowedModalIds: string[] = ['navigationModalId', 'scenarioModalId', 'inviteWithUrlModalId'];
  showModalIdList = false;
  lessonplanStepsList: string[] = [];

  // Sorting
  sortField: string = 'seqNo';
  sortDir: 'asc' | 'desc' = 'asc';
  sortedInteractivities: { item: any, index: number }[] = [];

  private recomputeSort() {
    if (!this.scenario?.interactivities) { this.sortedInteractivities = []; return; }
    const indexed = (this.scenario.interactivities as any[]).map((item, index) => ({ item, index }));
    this.sortedInteractivities = indexed.sort((a, b) => {
      let valA = a.item[this.sortField];
      let valB = b.item[this.sortField];
      if (valA == null) valA = '';
      if (valB == null) valB = '';
      const cmp = typeof valA === 'number' && typeof valB === 'number'
        ? valA - valB
        : String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
  }

  sortBy(field: string) {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.recomputeSort();
  }

  // Cache of discovered class names
  private _allowedInteractivityClasses: string[] | null = null;
  // Expose dynamically discovered class names (public for template)
  get allowedInteractivityClasses(): string[] {
    if (!this._allowedInteractivityClasses) {
      try {
        const base = (ScenarioDefs as any).InteractivityModel;
        const names: string[] = [];
        if (base) {
          for (const [exportName, value] of Object.entries(ScenarioDefs)) {
            if (!exportName.endsWith('Model') && exportName !== 'InteractivityModel') continue; // only look at *Model exports
            if (exportName === 'InteractivityModel') { names.push('Interactivity'); continue; }
            if (isCtor(value) && base && value.prototype instanceof base) {
              // convert RolesModel -> Roles etc.
              names.push(exportName.replace(/Model$/, ''));
            }
          }
        }
        // Deduplicate & sort
        this._allowedInteractivityClasses = Array.from(new Set(names)).sort();
      } catch {
        this._allowedInteractivityClasses = ['Interactivity'];
      }
    }
    return this._allowedInteractivityClasses;
  }
  showInteractivityClassList = false

  // Available component names from scenario
  get availableComponentNames(): string[] {
    if (!this.scenario?.components) return [];
    return this.scenario.components.map((comp: any) => comp.name || comp.id || '').filter((name: string) => name);
  }
  showComponentNameList: { [index: number]: boolean } = {};

  // Available sensor types from predefined sensor devices
  private sensors = [
    {
      name: "Gas 4 Monitor",
      imagePath: "sensors/gas4.png",
      sensors: [
        { name: "LEL", unit: "%", normalLevel: 0, dangerLevel: 10 },
        { name: "O2", unit: "%", normalLevel: 20.9, dangerLevel: 19.5 },
        { name: "CO", unit: "ppm", normalLevel: 0, dangerLevel: 35 },
        { name: "H2S", unit: "ppm", normalLevel: 0, dangerLevel: 10 }
      ]
    },
    {
      name: "Geiger Counter",
      imagePath: "sensors/Geiger.png",
      sensors: [
        { name: "Radiation", unit: "mR/hr", normalLevel: 0.02, dangerLevel: 5 },
        { name: "CPM", unit: "counts", normalLevel: 50, dangerLevel: 1000 }
      ]
    },
    {
      name: "Jerome Mercury Analyzer",
      imagePath: "sensors/Jerome Mercury.png",
      sensors: [
        { name: "Hg", unit: "mg/m³", normalLevel: 0, dangerLevel: 0.05 }
      ]
    },
    {
      name: "Ludlum Geiger Counter",
      imagePath: "sensors/Ludlum 2 Geiger.png",
      sensors: [
        { name: "Radiation", unit: "mR/hr", normalLevel: 0.02, dangerLevel: 5 },
        { name: "Beta", unit: "cpm", normalLevel: 50, dangerLevel: 500 },
        { name: "Gamma", unit: "cpm", normalLevel: 50, dangerLevel: 500 }
      ]
    },
    {
      name: "MiniRAE PID Monitor",
      imagePath: "sensors/MiniRAE PID Monitor.png",
      sensors: [
        { name: "PID", unit: "ppm", normalLevel: 0, dangerLevel: 1 },
        { name: "LEL", unit: "%", normalLevel: 0, dangerLevel: 10 },
        { name: "O2", unit: "%", normalLevel: 20.9, dangerLevel: 19.5 }
      ]
    },
    {
      name: "MSA Monitor",
      imagePath: "sensors/MSA Monitor.png",
      sensors: [
        { name: "LEL", unit: "%", normalLevel: 0, dangerLevel: 10 },
        { name: "O2", unit: "%", normalLevel: 20.9, dangerLevel: 19.5 },
        { name: "CO", unit: "ppm", normalLevel: 0, dangerLevel: 35 },
        { name: "H2S", unit: "ppm", normalLevel: 0, dangerLevel: 10 },
        { name: "SO2", unit: "ppm", normalLevel: 0, dangerLevel: 2 }
      ]
    },
    {
      name: "RAE 5 Monitor",
      imagePath: "sensors/RAE5.png",
      sensors: [
        { name: "PID", unit: "ppm", normalLevel: 0, dangerLevel: 1 },
        { name: "LEL", unit: "%", normalLevel: 0, dangerLevel: 10 },
        { name: "O2", unit: "%", normalLevel: 20.9, dangerLevel: 19.5 },
        { name: "CO", unit: "ppm", normalLevel: 0, dangerLevel: 35 }
      ]
    },
    {
      name: "Smart Sensor",
      imagePath: "sensors/Smart Sensor.png",
      sensors: [
        { name: "NH3", unit: "ppm", normalLevel: 0, dangerLevel: 25 },
        { name: "Cl2", unit: "ppm", normalLevel: 0, dangerLevel: 0.5 },
        { name: "HCl", unit: "ppm", normalLevel: 0, dangerLevel: 5 }
      ]
    },
    {
      name: "XLA Alert Monitor",
      imagePath: "sensors/XLA Alert.png",
      sensors: [
        { name: "LEL", unit: "%", normalLevel: 0, dangerLevel: 10 },
        { name: "O2", unit: "%", normalLevel: 20.9, dangerLevel: 19.5 },
        { name: "CO", unit: "ppm", normalLevel: 0, dangerLevel: 35 },
        { name: "H2S", unit: "ppm", normalLevel: 0, dangerLevel: 10 }
      ]
    }
  ];

  get availableSensorTypes(): string[] {
    const sensorNames: string[] = [];
    this.sensors.forEach(device => {
      device.sensors.forEach(sensor => {
        if (!sensorNames.includes(sensor.name)) {
          sensorNames.push(sensor.name);
        }
      });
    });
    return sensorNames.sort();
  }
  showSensorTypeList: { [index: number]: boolean } = {};

  // Current item being edited
  interactivityIndex = -1; // only existing items (no create mode)
  interactivity: any = {};
  interactivitytext = '';
  // Removed staging fields (direct binding now)

  // Class-specific temporary holders (if needed)

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scenario']) {
      this.recomputeSort();
    }
  }

  ngOnInit(): void {
    this.lessonplanStepsList = this.lessonplanSteps;
    this.recomputeSort();
  }

  // -------- list actions ----------
  addInteractivity() {
    if (!this.scenario.interactivities) {
      this.scenario.interactivities = [];
    }
    // derive next seqNo
    let nextSeq = 100;
    try {
      const seqs = this.scenario.interactivities.map((i: any) => Number(i.seqNo) || 0);
      if (seqs.length > 0) nextSeq = Math.max(...seqs) + 10;
    } catch { }
    const base: any = {
      class: 'Interactivity',
      name: '',
      enabled: true,
      component: '',
      seqNo: nextSeq
    };
    this.scenario.interactivities.push(base);
    this.recomputeSort();
    this.editInteractivity(base, this.scenario.interactivities.length - 1);
  }
  editInteractivity(interactivity: any, index: number) {
    this.closeAllInteractivityModals();
    this.interactivity = interactivity;
    // Migrate ppefiles from legacy string[] to {filename,title,description}[]
    if (Array.isArray(this.interactivity.ppefiles)) {
      this.interactivity.ppefiles = this.interactivity.ppefiles.map((f: any) =>
        typeof f === 'string' ? { filename: f, title: '', description: '' } : f
      );
    }
    this.interactivityIndex = index;
    this.interactivitytext = JSON.stringify(this.interactivity, null, 2);
    this.openModalForClass(interactivity.class);
  }

  async deleteInteractivity(index: number) {
    this.loader.show();
    try {
      this.scenario.interactivities.splice(index, 1);
      this.recomputeSort();
      if (this.onSaveInteractivity) { await this.onSaveInteractivity(); }
      if (this.onReloadInteractivity) { await this.onReloadInteractivity(); }
    } finally {
      this.loader.hide();
    }
  }

  // (Inventory & patches removed in new schema)

  // -------- save actions ----------
  async saveInteractivityFromJsonString() {
    this.showJsonError = false;
    try {
      const inter = JSON.parse(this.interactivitytext);
      this.interactivity = inter;
      if (this.interactivityIndex >= 0) {
        this.scenario.interactivities[this.interactivityIndex] = this.interactivity;
      }
      // Keep textarea in a formatted state after successful parse
      try { this.interactivitytext = JSON.stringify(this.interactivity, null, 2); } catch { }
      this.recomputeSort();
      this.closeAllInteractivityModals();
      if (this.onSaveInteractivity) { await this.onSaveInteractivity(); }
      if (this.onReloadInteractivity) { await this.onReloadInteractivity(); }
    } catch (_e) {
      this.showJsonError = true;
    } finally {
      this.loader.hide();
    }
  }

  // Optional: Pretty format current JSON text in the textarea
  formatInteractivityText() {
    this.showJsonError = false;
    try {
      const parsed = JSON.parse(this.interactivitytext);
      this.interactivitytext = JSON.stringify(parsed, null, 2);
    } catch (_e) {
      // Don’t flip the error flag here to avoid blocking edits; only visual formatting fails
      this.showJsonError = true;
    }
  }

  async saveInteractivity() {
    this.loader.show();
    this.showJsonError = false;
    try {
      if (this.interactivityIndex >= 0) {
        this.scenario.interactivities[this.interactivityIndex] = this.interactivity;
      }
      // Keep JSON textarea in sync so "Update from string" reflects current state
      try { this.interactivitytext = JSON.stringify(this.interactivity, null, 2); } catch { }
      this.recomputeSort();
      this.closeAllInteractivityModals();
      if (this.onSaveInteractivity) { await this.onSaveInteractivity(); }
      if (this.onReloadInteractivity) { await this.onReloadInteractivity(); }
    } catch (_e) {
      this.showJsonError = true;
    } finally {
      this.loader.hide();
    }
  }

  // -------- utils ----------
  closeAllInteractivityModals() {
    // Explicitly close all modal flags to ensure they work
    this.showInteractivity = false;
    this.showInteractivityGrass = false;
    this.showInteractivityFire = false;
    this.showInteractivitySmoke = false;
    this.showInteractivityGateOpenClose = false;
    this.showInteractivityWindDirection = false;
    this.showInteractivityWindHose = false;
    this.showInteractivityCompass = false;
    this.showInteractivityPPE = false;
    this.showInteractivitySensorSim = false;
    this.showInteractivityTracker = false;
    this.showInteractivityRoles = false;
    this.showInteractivityScenarioDescription = false;
    this.showInteractivitySickChar = false;
    this.showInteractivityModalDisplay = false;
    this.showInteractivityLabelDisplay = false;
    this.showInteractivityHyperlink = false;
    this.showInteractivityExploreArec = false;
    this.showInteractivityExploreMap = false;
    this.showInteractivityMap = false;
    this.showInteractivityApproach = false;
    this.showInteractivityTankertruck = false;
    this.showInteractivityManhole = false;
    this.showInteractivityPhaseInOut = false;
    this.showInteractivityPlayAnimation = false;
    this.showInteractivityPlayAudio = false;
    this.showInteractivityRotate = false;
    this.showInteractivityShowHide = false;
    this.showInteractivityBoiler = false;
    this.showInteractivitySewer = false;
    this.showInteractivityWeatherStation = false;
    this.showJsonError = false;
    this.showClassNameInfo = false;
    this.showInteractivityClassList = false;
  }

  // pick list handling
  pickModalHtmlFile(name: string) {
    if (!this.interactivity) { this.interactivity = {}; }
    (this.interactivity as any).modalHTMLFileName = name;
  }

  pickModalId(name: string) {
    if (!this.interactivity) { this.interactivity = {}; }
    (this.interactivity as any).modalId = name;
  }

  pickInteractivityClass(name: string) {
    if (this.interactivity) this.interactivity.class = name
    this.showInteractivityClassList = false
  }

  pickComponentId(componentName: string, index: number) {
    if (this.interactivity?.components && this.interactivity.components[index]) {
      this.interactivity.components[index].componentName = componentName;
    }
    this.showComponentNameList[index] = false;
  }

  pickSensorType(sensorType: string, index: number) {
    if (this.interactivity?.components && this.interactivity.components[index]) {
      this.interactivity.components[index].sensor = sensorType;
    }
    this.showSensorTypeList[index] = false;
  }

  addPPEFile(filename: string) {
    if (!filename) return;
    if (!this.interactivity.ppefiles) { this.interactivity.ppefiles = []; }
    this.interactivity.ppefiles.push({ filename, title: '', description: '' });
    try { this.interactivitytext = JSON.stringify(this.interactivity, null, 2); } catch { }
  }

  private openModalForClass(cls: string) {
    this.closeAllInteractivityModals();
    // Normalize
    let raw = (cls || '').trim();
    // Accept TS class names with Model suffix (e.g., RolesModel) by stripping the suffix for modal flag lookup
    if (raw.endsWith('Model')) {
      raw = raw.substring(0, raw.length - 5); // drop 'Model'
    }
    if (!raw || raw === 'Interactivity') { this.showInteractivity = true; return; }
    let prop = 'showInteractivity' + raw;
    if ((this as any)[prop] === undefined) {
      // Try case-insensitive match among existing showInteractivity* flags
      const target = Object.keys(this)
        .filter(k => k.startsWith('showInteractivity') && (this as any)[k] === false)
        .find(k => k.toLowerCase() === ('showInteractivity' + raw).toLowerCase());
      if (target) prop = target;
    }
    if ((this as any)[prop] !== undefined) {
      (this as any)[prop] = true;
    } else {
      // Fallback to base editor if no specific modal flag exists
      this.showInteractivity = true;
    }
  }

  // Methods for managing GateOpenClose open/close actions
  addOpenAction(): void {
    if (!this.interactivity.open) {
      this.interactivity.open = [];
    }
    this.interactivity.open.push('');
  }

  removeOpenAction(index: number): void {
    if (this.interactivity.open && this.interactivity.open.length > 1) {
      this.interactivity.open.splice(index, 1);
    }
  }

  addCloseAction(): void {
    if (!this.interactivity.close) {
      this.interactivity.close = [];
    }
    this.interactivity.close.push('');
  }

  removeCloseAction(index: number): void {
    if (this.interactivity.close && this.interactivity.close.length > 1) {
      this.interactivity.close.splice(index, 1);
    }
  }

  // Getter for all lesson plan steps
  get lessonplanSteps(): any[] {
    if (!this.scenario?.lessonplan) {
      return [];
    }
    return this.scenario.lessonplan;
  }

  // TrackBy functions to prevent input field recreation
  trackByOpenIndex(index: number, item: any): number {
    return index;
  }

  trackByCloseIndex(index: number, item: any): number {
    return index;
  }
}
